import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase/config';

export interface DirectChat {
  chatId: string;
  /** The other person's email, lower-cased. */
  other: string;
  lastSender: string;
  preview: string;
  at: number;
}

const readJson = (key: string): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

/**
 * The signed-in user's one-on-one FIN chats, keyed by the other person's email,
 * plus which of those have a message from the other person that hasn't been seen.
 */
export function useDirectChats(email?: string | null, uid?: string | null) {
  const me = (email || '').toLowerCase();
  const seenKey = `finDirectSeen:${uid || ''}`;
  const sinceKey = `finDirectSeenSince:${uid || ''}`;
  const [directChats, setDirectChats] = useState<Record<string, DirectChat>>({});
  const [readAt, setReadAt] = useState<Record<string, number>>({});
  const [seenAt, setSeenAt] = useState<Record<string, number>>(() => readJson(seenKey));
  // Only messages after this person first had the feature count, so years-old chats don't all light up.
  const [since] = useState<number>(() => {
    const saved = Number(localStorage.getItem(sinceKey));
    if (saved) return saved;
    const now = Date.now();
    localStorage.setItem(sinceKey, String(now));
    return now;
  });

  useEffect(() => {
    setSeenAt(readJson(seenKey));
  }, [seenKey]);

  useEffect(() => {
    if (!email) return;
    const perQuery: Record<string, Record<string, DirectChat>> = {};
    const emails = Array.from(new Set([email, me]));
    const unsubs = emails.map((e) =>
      onSnapshot(
        query(collection(db, 'chats'), where('participants', 'array-contains', e)),
        (snap) => {
          const map: Record<string, DirectChat> = {};
          snap.docs.forEach((d) => {
            const data = d.data();
            const people: string[] = (data.participants || []).map((p: string) => String(p).toLowerCase());
            if (data.isPrivate === false || people.length !== 2) return;
            const other = people.find((p) => p !== me);
            if (!other) return;
            const at = data.updatedAt?.toMillis?.() || 0;
            if (!map[other] || at > map[other].at) {
              map[other] = {
                chatId: d.id,
                other,
                lastSender: String(data.lastMessageSender || '').toLowerCase(),
                preview: data.lastMessage || '',
                at,
              };
            }
          });
          perQuery[e] = map;
          const merged: Record<string, DirectChat> = {};
          Object.values(perQuery).forEach((m) =>
            Object.values(m).forEach((c) => {
              if (!merged[c.other] || c.at > merged[c.other].at) merged[c.other] = c;
            })
          );
          setDirectChats(merged);
        },
        (err) => console.warn('Direct chats listener:', err.code)
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [email, me]);

  // Read positions saved by the FIN Chats page, so reading there clears the badge too.
  useEffect(() => {
    if (!email) return;
    return onSnapshot(
      query(collection(db, 'userChatData'), where('userEmail', '==', email)),
      (snap) => {
        const map: Record<string, number> = {};
        snap.docs.forEach((d) => {
          const t = d.data().lastReadTimestamp?.toMillis?.();
          if (d.data().chatId && t) map[d.data().chatId] = t;
        });
        setReadAt(map);
      },
      (err) => console.warn('Chat read positions listener:', err.code)
    );
  }, [email]);

  const markSeen = useCallback(
    (chatId: string) => {
      setSeenAt((prev) => {
        const next = { ...prev, [chatId]: Date.now() };
        try {
          localStorage.setItem(seenKey, JSON.stringify(next));
        } catch {
          /* storage full; badge just reappears next session */
        }
        return next;
      });
    },
    [seenKey]
  );

  const unreadFrom = useMemo(() => {
    const out: Record<string, DirectChat> = {};
    Object.values(directChats).forEach((c) => {
      if (c.lastSender !== c.other) return;
      const seen = Math.max(seenAt[c.chatId] || 0, readAt[c.chatId] || 0, since);
      if (c.at > seen) out[c.other] = c;
    });
    return out;
  }, [directChats, seenAt, readAt, since]);

  return { directChats, unreadFrom, markSeen };
}
